import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import {
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
  SelectChangeEvent,
  Stack,
} from '@mui/material';

import colors from '@/theme/variables/colors';

import { FormElementProps } from '../types';

export type RadioItem = {
  label: string;
  value: string | number;
  disabled?: boolean;
};
export interface FormRadiosFieldProps
  extends Omit<RadioGroupProps, 'name' | 'onChange' | 'variant'>,
    FormElementProps {
  label?: string;
  items?: RadioItem[];
  handleChange?: (name: string, value: string | number) => void;
}

export const FormRadiosField = (props: FormRadiosFieldProps) => {
  const {
    defaultValue = '',
    handleChange,
    items = [],
    label,
    name,
    rules,
    ...rest
  } = props;
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;

  return (
    <Stack className="radios-wrapper">
      <InputLabel>{label}</InputLabel>
      <Controller
        control={control}
        name={name}
        render={({ field }: any) => {
          return (
            <RadioGroup
              sx={(theme) => ({
                border: `1px solid ${colors.grey[20]}`,
                borderRadius: '8px',
                '& label': {
                  borderBottom: `1px solid ${colors.grey[20]}`,
                  padding: '15px',
                  margin: 0,
                },
                '& label:last-child': {
                  borderBottom: 0,
                },
                '& label:has(input[type="radio"]:checked)': {
                  backgroundColor:
                    theme.palette.mode === 'light'
                      ? colors.grey[10]
                      : colors.grey[80],
                },
              })}
              {...rest}
              defaultValue={defaultValue}
              value={field.value}
              onChange={(event: SelectChangeEvent) => {
                if (handleChange != null)
                  handleChange(event.target.name, event.target.value);
                field.onChange(event);
              }}
            >
              {items.map((radioItem, index) => (
                <FormControlLabel
                  sx={{
                    '& span': {
                      pl: 0,
                      pr: '4px',
                      fontSize: '14px',
                    },
                    '& span span': {
                      color: colors.error.main,
                    },
                    '& span svg': {
                      width: '20px',
                      height: '20px',
                    },
                  }}
                  control={
                    <Radio disabled={radioItem.disabled} sx={{ py: 0 }} />
                  }
                  key={index}
                  label={radioItem.label}
                  value={radioItem.value}
                />
              ))}
            </RadioGroup>
          );
        }}
        rules={rules}
        defaultValue={defaultValue}
      />
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </Stack>
  );
};
